import { memo, useState } from 'react';
import { faCheck, faCopy, faLink } from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';

import { AnyObject } from '@typings';
import {
  APP_INSTANCE_REF_LITERAL,
  CUSTOM_COMPONENT_NAME,
} from '@constants/shared.constants';
import { platformAppsInstancesSelector } from '@selectors';
import { useSelector } from '@hooks';
import {
  isArray,
  isBoolean,
  isNumber,
  isObject,
  isString,
  joinNonEmptyStrings,
  path,
} from '@utils';

import { Button, Icon, Link, Render } from '@components/Shared';

import { SchemaParamReference } from './SchemaParamReference';

type Props = {
  isSchemaArray?: boolean;
  isTopLevel?: boolean;
  schema: AnyObject;
  params: AnyObject;
  prefix?: string;
};

export const ParamsSchema = memo<Props>(
  ({ isSchemaArray, isTopLevel, schema, params, prefix = '' }) => {
    const instances = useSelector(platformAppsInstancesSelector);

    const [collapsedItems, setCollapsedItems] = useState<Set<string>>(
      new Set(),
    );
    const [copied, setCopied] = useState(false);

    const toggleCollapse = (key: string) => {
      setCollapsedItems((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(key)) {
          newSet.delete(key);
        } else {
          newSet.add(key);
        }
        return newSet;
      });
    };

    const handleCopy = async (value: any) => {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);

      setTimeout(() => setCopied(false), 2000);
    };

    const renderFormattedValue = (value: any) => {
      if (isBoolean(value)) {
        const formattedBooleanValue = value ? 'true' : 'false';

        return <span className="text-pink-800">{formattedBooleanValue}</span>;
      }

      if (value === null) {
        return <span className="text-neural-06">null</span>;
      }

      if (value === '') {
        return <span className="text-lime-800">&quot;&quot;</span>;
      }

      if (isString(value)) {
        return <span className="text-lime-800">{value}</span>;
      }

      if (isNumber(value)) {
        return <span className="text-cyan-800">{value}</span>;
      }

      return value;
    };

    const renderAppIntegration = (instanceId: string) => {
      const instance = instances.find(({ id }) => id === instanceId);
      const title = instance?.displayName || instance?.name || instanceId;

      return (
        <Link
          theme
          external
          blank
          to={path.appsInstance(instanceId)}
          className="flex items-center gap-1.5 text-primary"
        >
          {title} <Icon icon={faLink} className="text-[18px]" />
        </Link>
      );
    };

    const makeItem = ([key, param]: [string, any]) => {
      const schemaKey = isSchemaArray ? '[]' : key;
      const fullKey = joinNonEmptyStrings(prefix, schemaKey);

      const metadata = params[fullKey];

      const isReference = Object.values(CUSTOM_COMPONENT_NAME).some(
        ([type, propertyName]) =>
          type === metadata?.type && propertyName === schemaKey,
      );

      const isAppIntegration =
        isObject(param) && param.type === APP_INSTANCE_REF_LITERAL;

      const isIntegration = isAppIntegration || isReference;
      const isSchema = (isObject(param) || isArray(param)) && !isReference;

      const itemKey = joinNonEmptyStrings(prefix, key);
      const isCollapsed = collapsedItems.has(itemKey);

      const isSimpleValue = !isSchema && !isReference;

      return (
        <div key={itemKey} className="">
          <Button
            theme
            disabled={isSimpleValue}
            className={clsx('relative flex flex-col', {
              'font-bold': isTopLevel,
              'cursor-pointer': isSchema || isIntegration,
              'cursor-default': isSimpleValue,
            })}
            onClick={() => toggleCollapse(itemKey)}
          >
            <Render if={!isSimpleValue}>
              <Icon
                icon={faCaretDown}
                className={clsx(
                  'absolute right-full top-0 text-xs transition-transform duration-200',
                  {
                    'rotate-[-90deg]': isCollapsed,
                  },
                )}
              />
            </Render>
            <h4>
              {metadata?.title ?? key}
              <Render if={isSchemaArray}> ({key})</Render>
            </h4>
          </Button>

          <div
            className={clsx('overflow-hidden', {
              contents: !isCollapsed,
              'h-0': isCollapsed,
            })}
          >
            <Render if={isSchema}>
              <div className="flex flex-col gap-4 pl-4">
                <ParamsSchema
                  isSchemaArray={isArray(param)}
                  key={fullKey}
                  prefix={fullKey}
                  params={params}
                  schema={param}
                />
              </div>
            </Render>

            <Render if={!isSchema && !isReference}>
              <Button
                theme
                variant="ghost"
                onClick={() => handleCopy(param)}
                className="group inline-block p-0 text-left hover:bg-transparent"
              >
                <span>{renderFormattedValue(param)}</span>
                <Icon
                  icon={copied ? faCheck : faCopy}
                  className={clsx(
                    'ml-1 inline-flex translate-y-px opacity-0 transition-opacity group-hover:opacity-100',
                    {
                      'text-success': copied,
                      'text-neural-04': !copied,
                    },
                  )}
                />
              </Button>
            </Render>

            <Render if={isAppIntegration}>
              {renderAppIntegration(param?.instance_id as string)}
            </Render>

            <Render if={isIntegration}>
              <SchemaParamReference
                schemaKey={schemaKey}
                typeName={metadata?.type}
                value={param}
              />
            </Render>
          </div>
        </div>
      );
    };

    return (
      <div className="mt-2 flex flex-col gap-4">
        {Object.entries(schema).map(makeItem)}
      </div>
    );
  },
);

ParamsSchema.displayName = 'ParamsSchema';
